import { Check } from '@phosphor-icons/react';
import { LANGUES_DU_MONDE, LANGUES_PARLEES, type LangueParlee } from '@sirocco/shared';
import { useMemo, useRef, useState, type TouchEvent } from 'react';
import { locale, t } from '../../i18n';
import { useOrdreFige } from '../../hooks/useOrdreFige';
import { cocheesDAbord, nomLocal, sansAccents } from '../../utils/langues';
import { SearchField } from './Field';

interface Ligne {
  code: LangueParlee;
  nom: string;
  drapeau: string;
  /** Le nom dans la langue de l'application, s'il en differe. */
  local: string | null;
}

/**
 * Toutes les langues, une recherche au-dessus, des coches a cote.
 *
 * Le meme catalogue sert a dire les langues d'une sortie, sur toute la
 * hauteur de l'ecran, et a filtrer la liste des sorties, dans la petite
 * fenetre des filtres : quarante langues ne tiennent pas en pastilles.
 *
 * Une seule liste, par ordre alphabetique du nom de chaque langue dans
 * celle de l'application : c'est ainsi qu'on la cherche des yeux. Celles
 * qui sont deja cochees passent en tete, dans le meme ordre, puis toutes
 * les autres. La recherche trouve aussi par le nom dans la langue de
 * l'application, « allemand » pour « Deutsch ».
 */
export function CatalogueDeLangues({
  valeur,
  onChange,
  auMoinsUne,
  max,
  className = '',
}: {
  valeur: readonly LangueParlee[];
  onChange: (langues: LangueParlee[]) => void;
  /**
   * Une sortie a toujours une langue : la derniere cochee ne se decoche
   * pas. Un filtre peut n'en avoir aucune, ce qui veut dire toutes.
   */
  auMoinsUne: boolean;
  /** Combien on peut en cocher au plus. Sans borne pour un filtre. */
  max?: number;
  className?: string;
}) {
  const [recherche, setRecherche] = useState('');
  /* Chaque appui sur une langue grisee secoue la phrase de la borne : c'est elle qui dit pourquoi rien ne se passe. */
  const [secousse, setSecousse] = useState(0);
  /* Ou le doigt s'est pose dans la liste, pour savoir s'il defile. */
  const departDefile = useRef<number | null>(null);

  const lignes = useMemo(
    () =>
      LANGUES_DU_MONDE.map((langue): Ligne => {
        const local = nomLocal(langue.code);
        return {
          code: langue.code,
          nom: langue.nom,
          drapeau: langue.drapeau,
          local: local && sansAccents(local) !== sansAccents(langue.nom) ? local : null,
        };
      }).sort((a, b) => (a.local ?? a.nom).localeCompare(b.local ?? b.nom, locale)),
    [],
  );

  /* La borne atteinte, les autres langues se grisent : on decoche avant d'en prendre une autre. */
  const plein = max !== undefined && valeur.length >= max;

  const { ordre, figer } = useOrdreFige(cocheesDAbord(lignes, (ligne) => valeur.includes(ligne.code)));

  const mot = sansAccents(recherche.trim());
  const affichees = mot
    ? ordre.filter(
        (ligne) =>
          sansAccents(ligne.nom).includes(mot) ||
          (ligne.local !== null && sansAccents(ligne.local).includes(mot)) ||
          ligne.code === mot,
      )
    : ordre;

  function basculer(code: LangueParlee) {
    const cochee = valeur.includes(code);
    if (cochee && auMoinsUne && valeur.length === 1) return;
    if (!cochee && plein) {
      setSecousse((avant) => avant + 1);
      return;
    }
    figer();
    const suivante = cochee ? valeur.filter((autre) => autre !== code) : [...valeur, code];
    onChange(LANGUES_PARLEES.filter((autre) => suivante.includes(autre)));
  }

  /*
    Faire defiler la liste range le clavier, comme partout sur iPhone :
    il couvrait la moitie des langues qu'on voulait parcourir. Quelques
    pixels de marge, pour qu'un simple appui sur une langue ne le ferme pas.
  */
  function surDebutDefile(event: TouchEvent<HTMLDivElement>) {
    departDefile.current = event.touches[0]?.clientY ?? null;
  }

  function surDefile(event: TouchEvent<HTMLDivElement>) {
    const depart = departDefile.current;
    const doigt = event.touches[0];
    if (depart === null || !doigt || Math.abs(doigt.clientY - depart) < 8) return;
    const actif = document.activeElement;
    if (actif instanceof HTMLInputElement) actif.blur();
    departDefile.current = null;
  }

  return (
    <div className={`langues-catalogue ${className}`.trim()}>
      <SearchField
        tone="spot"
        className="langues-catalogue__recherche"
        placeholder={t.sorties.creation.languesChercher}
        value={recherche}
        onChange={(event) => setRecherche(event.target.value)}
        onClear={() => setRecherche('')}
      />
      {plein && (
        <p
          key={secousse}
          role="status"
          className={`langues-catalogue__plein${secousse ? ' langues-catalogue__plein--secoue' : ''}`}
        >
          {t.sorties.creation.languesMax}
        </p>
      )}
      <div className="langues-catalogue__defile" onTouchStart={surDebutDefile} onTouchMove={surDefile}>
        {affichees.length ? (
          <ul className="langues-catalogue__groupe">
            {affichees.map((ligne) => {
              const on = valeur.includes(ligne.code);
              return (
                <li key={ligne.code}>
                  <button
                    type="button"
                    className={`langues-catalogue__ligne${!on && plein ? ' langues-catalogue__ligne--bloquee' : ''}`}
                    aria-pressed={on}
                    aria-disabled={!on && plein ? true : undefined}
                    onClick={() => basculer(ligne.code)}
                  >
                    <span className="drapeau langues-catalogue__drapeau" aria-hidden="true">
                      {ligne.drapeau}
                    </span>
                    <span className="langues-catalogue__noms">
                      <span className="langues-catalogue__nom" lang={ligne.code}>
                        {ligne.nom}
                      </span>
                      {ligne.local && <span className="langues-catalogue__local">{ligne.local}</span>}
                    </span>
                    {on && <Check size={20} weight="bold" className="langues-catalogue__coche" />}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="langues-catalogue__vide">{t.sorties.creation.languesAucune}</p>
        )}
      </div>
    </div>
  );
}
